import { Grid, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { VALIDATION_ERR_DELAY } from '../../../config/AppConstants';
import { CreateBillingAddressDto } from '../../../lib/api/addresses/dto/create-billing-address.dto';
import { UpdateBillingAddressDto } from '../../../lib/api/addresses/dto/update-billing-address.dto';
import { States } from '../../../lib/api/addresses/enums/states.enum';
import RhfAutocomplete from '../../ui/forms/RhfAutocomplete';
import RhfTextField from '../../ui/forms/RhfTextField';

export default function BillingAddressPartial() {
  const {
    formState: { dirtyFields },
    setFocus,
    watch,
    trigger,
    control,
  } = useFormContext<CreateBillingAddressDto | UpdateBillingAddressDto>();

  useEffect(() => {
    setFocus('name');
  }, [setFocus]);

  //poBox, street1, and street2 fields have
  //cross field validation. I'll watch them
  //and trigger validation on all three when
  //any of them change.
  const watchPOBox = watch('poBox');
  const watchStreet1 = watch('street1');
  const watchStreet2 = watch('street2');

  const debouncedCrossValidationTrigger = useMemo(
    () =>
      debounce(() => {
        if (dirtyFields.street1 || dirtyFields.street2 || dirtyFields.poBox) {
          trigger('street1');
          trigger('street2');
          trigger('poBox');
        }
      }, VALIDATION_ERR_DELAY),
    [trigger, dirtyFields],
  );

  useEffect(() => {
    debouncedCrossValidationTrigger();
  }, [watchPOBox, watchStreet1, watchStreet2, debouncedCrossValidationTrigger]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography component="h3" variant="h5">
          Address
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <RhfTextField
          control={control}
          name="name"
          label="Bill to Company Name"
          required
        />
      </Grid>

      <Grid item xs={4}>
        <RhfTextField
          control={control}
          name="code"
          label="QuickBooks Code"
          required
          upperCase
        />
      </Grid>
      <Grid item xs={12}>
        <RhfTextField control={control} name="street1" label="Street" />
      </Grid>
      <Grid item xs={12}>
        <RhfTextField control={control} name="street2" label="Street 2" />
      </Grid>
      <Grid item xs={12} sm={12}>
        <RhfTextField control={control} name="poBox" label="PO Box #" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <RhfTextField control={control} name="city" label="City" required />
      </Grid>
      <Grid item xs={6} sm={3}>
        <RhfAutocomplete
          control={control}
          name="state"
          defaultText="State"
          optionsAsEnum={States}
          required
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <RhfTextField control={control} name="zip" label="Zip" required />
      </Grid>
    </Grid>
  );
}
